import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, effect, inject, signal, viewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Observable } from 'rxjs';

import { ListResponse } from '@models/helpers';
import { DEFAULT_INFINITE_PAGE, PAGE_SIZE_OPTIONS } from '@shared/consts';
import { UserStore } from '@signal-store/user.store';

/* 
    TODO: Check Material Scrolling docs and check the utility / implementation for custom dataSource.
    See how it can be implemented in paralel with pagination and infinite scroll.
*/
@Component({
  selector: 'cipo-virtual-list',
  template: '',
})
export abstract class VirtualListComponent<T> {
  userStore = inject(UserStore);
  viewport = viewChild(CdkVirtualScrollViewport);
  paginator = viewChild<MatPaginator>('paginator');

  items = signal<T[]>([]);
  recordsCount = signal(0);
  isLoading = signal(true);
  pageSizeOptions = PAGE_SIZE_OPTIONS.default;
  pageSize = signal(PAGE_SIZE_OPTIONS.default[0]);
  skeletonArray = signal(Array.from({ length: PAGE_SIZE_OPTIONS.default[0] }));
  isInfiniteScroll = signal<boolean>(null);
  onlyInfiniteScroll = false;
  nextPage = 1;
  threshold = 200;

  /* 
    To be aware of this. It must be called before any other effects in the subclass component. 
    It seems like it works as expected.
*/
  infiniteScrollEffect = effect(
    () => {
      const infiniteScroll = this.userStore.userData()?.loadMore;
      if (infiniteScroll === null || infiniteScroll === undefined) {
        return;
      }

      this.isInfiniteScroll.set(infiniteScroll);
      if (infiniteScroll || this.onlyInfiniteScroll) {
        this.skeletonArray.set([1]);
        this.pageSize.set(DEFAULT_INFINITE_PAGE);
        this.loadMoreVirtualItems();
      } else {
        this.loadPagedItems({ pageIndex: 0, pageSize: this.pageSize() });
      }
      this.infiniteScrollEffect.destroy();
    },
    { manualCleanup: true },
  );

  abstract loadItems(page: number, pagesize: number): Observable<ListResponse<T>>;

  abstract virtualTrackBy(index: number, item: T): string | number;

  loadMoreVirtualItems() {
    const page = this.nextPage;
    this.isLoading.set(true);
    this.loadItems(page, DEFAULT_INFINITE_PAGE).subscribe(({ data, records }) => {
      this.items.update(items => {
        return items.concat(data);
      });
      /**
       * This fixes a issue where the viewport size is not calculated correctly, loading only the offset
       * reproduced only on local environment, randomly.
       */
      setTimeout(() => {
        this.viewport()?.checkViewportSize();
      });
      this.nextPage++;
      this.recordsCount.set(records);
      this.isLoading.set(false);
    });
  }

  loadPagedItems({ pageIndex, pageSize: pagesize }: Partial<PageEvent>) {
    this.isLoading.set(true);
    let page = pageIndex + 1;
    if (this.pageSize() !== pagesize) {
      this.pageSize.set(pagesize);
      this.skeletonArray.set(Array.from({ length: pagesize }));
      page = 1;
    }
    this.items.set([]);
    this.loadItems(page, pagesize).subscribe(({ data, records }) => {
      this.items.set(data);
      setTimeout(() => {
        this.viewport()?.checkViewportSize();
      });
      this.recordsCount.set(records);
      this.isLoading.set(false);
    });
  }

  refreshList() {
    this.items.set([]);
    if (this.isInfiniteScroll()) {
      this.nextPage = 1;
      this.loadMoreVirtualItems();
    } else if (!this.paginator() || this.paginator()?.pageIndex === 0) {
      this.loadPagedItems({ pageIndex: 0, pageSize: this.pageSize() });
    } else {
      this.paginator()?.firstPage();
    }
  }

  onScroll() {
    if (
      this.items().length === this.recordsCount() ||
      !(this.userStore.userData()?.loadMore || this.onlyInfiniteScroll)
    ) {
      return;
    }
    const scrollOffset = this.viewport()?.measureScrollOffset('bottom');
    if (scrollOffset < this.threshold && !this.isLoading()) {
      this.loadMoreVirtualItems();
    }
  }
}
